import React, { forwardRef, useRef, useState, useEffect } from 'react';
import { Box, Button, FormControl, FormLabel, Chip } from '@mui/material';
import * as yup from 'yup';
import { type SubmitHandler } from 'react-hook-form';
import { connect, type ConnectedProps } from 'react-redux';
import { compose } from '@reduxjs/toolkit';
import { openDrawer, drawerType } from '@/core/features/drawer/state';
import Form, { type FormElement } from '@/components/Form';
import TextField from '@/components/TextField';
import SelectField from '@/components/SelectField';
import Editor from '@/components/Editor';
import { CloseIcon, PlusIcon } from '@/static';
import {
  eventTypeOptions,
  eventStatusOptions,
  sportOptions,
  tryFetchEventOptions,
} from '@/slices/events';
import { membersByPropertyId, tryFetchMembers } from '@/slices/members';
import { normalizeMemberOptions } from '@/routes/CreateEvent/utils';
import { type RootState } from '@/store';
import { useYupValidationResolver, normalizeSimpleFormOptions } from '@/utils';
import styles from './styles';

const validationSchema = yup.object({
  name: yup.string().required('Name is required'),
  event_type_id: yup.string().required('Event type is required'),
  event_status_id: yup.string().required('Event status is required'),
  sport_id: yup.string().when('event_type_id', {
    is: '1',
    then: (schema) => schema.required('Sport is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  contributors: yup.array().of(yup.string()),
  commentators: yup.string().optional(),
});

export type FormValues = {
  name: string;
  event_type_id: string;
  event_status_id: string;
  sport_id?: string;
  contributors: string[];
  commentators?: string;
};

export const defaultValues: FormValues = {
  name: '',
  event_type_id: '',
  sport_id: '',
  event_status_id: '',
  contributors: [],
  commentators: '',
};

type Props = ConnectedProps<typeof connector> & {
  editorId: string;
  propertyId: string;
  defaultValues?: FormValues;
  editorValue?: string;
  onEditorChange?: (content: string) => void;
  onSubmit: SubmitHandler<FormValues>;
};

const EventForm = (
  {
    editorId,
    propertyId,
    defaultValues: initialValues = defaultValues,
    editorValue = '',
    onEditorChange = () => {},
    onSubmit,
    eventTypeOptions,
    eventStatusOptions,
    sportOptions,
    members,
    openDrawer,
    currentDrawerType,
  }: Props,
  ref: React.Ref<FormElement>,
) => {
  const resolver = useYupValidationResolver(validationSchema);
  const [formValues, setFormValues] = useState<FormValues>(initialValues);
  const formRef = useRef<FormElement>(null);

  useEffect(() => {
    setFormValues(initialValues);
  }, [initialValues]);

  const setRef = (el: FormElement) => {
    formRef.current = el;

    if (typeof ref === 'function') {
      ref(el);
    } else if (ref) {
      ref.current = el;
    }
  };

  const handleFormChange = (values: FormValues) => {
    setFormValues(values);
  };

  const handleSubmit = () => {
    onSubmit(formValues);
  };

  const handleAddContributors = () => {
    openDrawer({
      drawerType: 'addContributors',
      drawerData: {
        propertyId,
        currentContributors: formValues.contributors,
        preservedFormState: formValues,
        preservedEditorValue: editorValue,
        sourceDrawerType: currentDrawerType,
        onAddContributors: (contributorIds: string[]) => {
          const updatedValues = {
            ...formValues,
            contributors: contributorIds,
          };
          setFormValues(updatedValues);

          if (formRef.current) {
            formRef.current.setValue('contributors', contributorIds);
          }

          setTimeout(() => {
            openDrawer({
              drawerType: currentDrawerType,
              drawerData: {
                propertyId,
                preservedFormState: updatedValues,
                preservedEditorValue: editorValue,
              },
            });
          }, 100);
        },
      },
    });
  };

  const handleRemoveContributor = (contributorToRemove: string) => {
    const updatedContributors = formValues.contributors.filter(
      (contributor) => contributor !== contributorToRemove,
    );
    const updatedValues = { ...formValues, contributors: updatedContributors };
    setFormValues(updatedValues);
  };

  const getContributorLabel = (contributorValue: string) => {
    const memberOptions = normalizeMemberOptions(members || []);
    const contributor = memberOptions.find(
      (option) => option.value === contributorValue,
    );
    return contributor?.label || contributorValue;
  };

  return (
    <Form
      key={`${initialValues.name}-${initialValues.contributors?.join(',') || ''}`}
      ref={setRef}
      sx={styles.form}
      resolver={resolver}
      defaultValues={initialValues}
      onChange={handleFormChange}
      onSubmit={handleSubmit}
    >
      <TextField name="name" label="Name" />

      <SelectField
        name="event_type_id"
        label="Event Type"
        options={normalizeSimpleFormOptions(eventTypeOptions)}
      />

      {formValues.event_type_id === '1' && (
        <SelectField
          name="sport_id"
          label="Sport"
          options={normalizeSimpleFormOptions(sportOptions)}
        />
      )}

      <FormControl>
        <FormLabel sx={styles.editorLabel}>Default Incident</FormLabel>

        <Editor
          id={editorId}
          value={editorValue}
          onChange={onEditorChange}
          placeholder="Enter your message..."
        />
      </FormControl>

      <TextField
        name="commentators"
        label="Commentators"
        placeholder="Στέφανος Τάτσιος[https://www.athletiko.gr/stefanos-tatsios]"
        helperText="Optional link: Name[https://url]"
      />

      <SelectField
        name="event_status_id"
        label="Status"
        options={normalizeSimpleFormOptions(eventStatusOptions)}
      />

      <Box sx={styles.contributorsWrapper}>
        <Button
          startIcon={<PlusIcon />}
          variant="text"
          size="large"
          onClick={handleAddContributors}
        >
          Add Contributors
        </Button>
      </Box>

      {formValues.contributors.length > 0 && (
        <Box sx={styles.contributorsChips}>
          {formValues.contributors.map((contributor) => (
            <Chip
              key={contributor}
              label={getContributorLabel(contributor)}
              onDelete={() => handleRemoveContributor(contributor)}
              deleteIcon={<CloseIcon />}
            />
          ))}
        </Box>
      )}
    </Form>
  );
};

const connector = connect(
  (state: RootState, { propertyId }: { propertyId: string }) => ({
    eventTypeOptions: eventTypeOptions(state),
    eventStatusOptions: eventStatusOptions(state),
    sportOptions: sportOptions(state),
    members: membersByPropertyId(state, { propertyId }),
    currentDrawerType: drawerType(state),
  }),
  {
    tryFetchEventOptions,
    tryFetchMembers,
    openDrawer,
  },
  null,
  { forwardRef: true },
);

export default compose(connector, forwardRef<FormElement, Props>)(EventForm);
